import React, { useState, useEffect, useCallback } from 'react';
import { getNivelFidelidadById, createNivelFidelidad, updateNivelFidelidad } from '../services/nivelFidelidadService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, RefreshCw, Check, AlertCircle, Award, Percent, Star } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const NivelFidelidadForm = () => {
    const { id } = useParams();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);

    const [form, setForm] = useState({
        codigo: '',
        descripcion: '',
        descuentoTasa: 0.00,
        puntosMin: 0,
        puntosMax: 0
    });

    const fetchNivel = useCallback(async () => {
        if (!isEditMode) return;
        try {
            setFetching(true);
            const data = await getNivelFidelidadById(token, id);
            setForm({
                codigo: data.codigo,
                descripcion: data.descripcion,
                descuentoTasa: data.descuentoTasa || 0.00,
                puntosMin: data.puntosMin || 0,
                puntosMax: data.puntosMax || 0
            });
        } catch (error) {
            toast.error('Error al cargar datos del nivel de fidelidad');
            navigate('/parametrizacion/niveles-fidelidad');
        } finally {
            setFetching(false);
        }
    }, [id, isEditMode, token, navigate]);

    useEffect(() => {
        fetchNivel();
    }, [fetchNivel]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.codigo.trim() || !form.descripcion.trim()) {
            toast.error('Código y descripción son obligatorios');
            return;
        }

        try {
            setLoading(true);
            const dataToSend = {
                codigo: form.codigo.toUpperCase(),
                descripcion: form.descripcion,
                descuentoTasa: parseFloat(form.descuentoTasa),
                puntosMin: parseInt(form.puntosMin),
                puntosMax: parseInt(form.puntosMax)
            };

            if (isEditMode) {
                await updateNivelFidelidad(token, id, dataToSend);
                toast.success('Nivel de fidelidad actualizado');
            } else {
                await createNivelFidelidad(token, dataToSend);
                toast.success('Nivel de fidelidad creado');
            }
            setTimeout(() => navigate('/parametrizacion/niveles-fidelidad'), 1000);
        } catch (error) {
            toast.error('Error al guardar. Verifique si el código ya existe.');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <RefreshCw className="w-10 h-10 animate-spin text-blue-600" />
                <p className="text-slate-500 font-bold uppercase text-xs tracking-widest italic">Sincronizando...</p>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-xl border border-slate-300 mb-10 overflow-hidden rounded-none animate-in fade-in duration-500">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-4 flex items-center justify-between text-white shadow-inner border-b border-slate-800">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/parametrizacion/niveles-fidelidad')}
                        className="p-1.5 hover:bg-slate-600 transition-colors text-slate-300 hover:text-white"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-lg font-black tracking-tight uppercase italic">
                            {isEditMode ? 'Mantenimiento: ' + form.codigo : 'Nuevo Nivel de Fidelidad'}
                        </h1>
                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.3em]">Módulo de Segmentación y Beneficios de Socios</p>
                    </div>
                </div>
                <div className="bg-slate-600 px-3 py-1 text-[10px] font-black uppercase border border-slate-500 shadow-sm">Mv-FID-0.1</div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 bg-[#f8fafc]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

                    {/* IZQUIERDA: Definición */}
                    <div className="space-y-8">
                        <div className="bg-white border border-slate-200 p-6 relative overflow-hidden shadow-sm">
                            <div className="absolute top-0 left-0 w-1 h-full bg-slate-700"></div>
                            <div className="flex items-center gap-2 mb-6 border-b border-slate-100 pb-2">
                                <Award className="w-4 h-4 text-slate-400" />
                                <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Atributos del Nivel</h3>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Código Identificador (Ej: BRONCE, PLATA, ORO) <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        required
                                        maxLength="20"
                                        className="w-full px-4 py-3 border border-slate-300 focus:border-blue-600 outline-none font-black uppercase bg-slate-50 focus:bg-white transition-all text-[13px] rounded-none shadow-sm"
                                        placeholder="Ej: BRONCE"
                                        value={form.codigo}
                                        onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                                        disabled={isEditMode} // Usually codes are read-only upon edit
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Descripción / Nombre del Nivel <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-4 py-3 border border-slate-300 focus:border-blue-600 outline-none font-bold uppercase bg-white focus:bg-white transition-all text-[13px] rounded-none shadow-sm"
                                        placeholder="Ej: SOCIO BRONCE - NIVEL BÁSICO"
                                        value={form.descripcion}
                                        onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Descuento en Tasa de Interés (%)</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            className="w-full pl-10 pr-4 py-3 border border-slate-300 focus:border-blue-600 outline-none font-black bg-slate-50 focus:bg-white transition-all text-[13px] rounded-none shadow-sm"
                                            value={form.descuentoTasa}
                                            onChange={(e) => setForm({ ...form, descuentoTasa: e.target.value })}
                                        />
                                        <Percent className="absolute left-3 top-3.5 w-4 h-4 text-slate-300" />
                                    </div>
                                    <p className="text-[9px] text-slate-400 mt-1 font-bold uppercase">Ej: 0.50 para restar 0.50% a la tasa final de préstamo asignada.</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Puntos Mínimos</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                min="0"
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 focus:border-blue-600 outline-none font-black bg-slate-50 focus:bg-white transition-all text-[13px] rounded-none shadow-sm"
                                                value={form.puntosMin}
                                                onChange={(e) => setForm({ ...form, puntosMin: e.target.value })}
                                            />
                                            <Star className="absolute left-3 top-3.5 w-4 h-4 text-slate-300" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Puntos Máximos</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                min="0"
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 focus:border-blue-600 outline-none font-black bg-slate-50 focus:bg-white transition-all text-[13px] rounded-none shadow-sm"
                                                value={form.puntosMax}
                                                onChange={(e) => setForm({ ...form, puntosMax: e.target.value })}
                                            />
                                            <Star className="absolute left-3 top-3.5 w-4 h-4 text-slate-300" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* DERECHA: Guía */}
                    <div className="space-y-6 bg-slate-200/50 p-6 border border-slate-300 shadow-inner">
                        <div className="text-center mb-4">
                            <h2 className="text-xs font-black text-slate-700 uppercase tracking-[0.2em]">Manual de Puntos y Fidelización</h2>
                            <div className="h-1 w-12 bg-indigo-600 mx-auto mt-1"></div>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-slate-200 p-5 shadow-sm hover:border-indigo-400 transition-all">
                                <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1 border-b border-slate-100 pb-1">
                                    <AlertCircle className="w-3.5 h-3.5 text-indigo-500" />
                                    ¿Cómo funciona?
                                </h3>
                                <p className="text-[11px] text-slate-600 font-bold uppercase leading-relaxed">
                                    El nivel de fidelidad de un socio se calcula automáticamente en base a sus aportes, regularidad de pagos, y saldo en caja de ahorro. 
                                    Este nivel puede aplicarle descuentos directos sobre la tasa de interés ordinaria al solicitar un crédito.
                                </p>
                            </div>

                            <div className="bg-amber-50 border border-amber-200 p-4 shadow-sm flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
                                <div className="text-[10px] text-amber-800 font-bold uppercase leading-relaxed">
                                    <p className="mb-1">⚠️ ATENCIÓN CON RANGOS:</p>
                                    Asegúrese de que los rangos de puntos no se solapen con otros niveles de fidelidad para evitar conflictos durante el cálculo automático de los socios.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-12 pt-6 border-t border-slate-300 flex justify-between items-center">
                    <button
                        type="button"
                        onClick={() => navigate('/parametrizacion/niveles-fidelidad')}
                        className="px-8 py-2 border border-slate-300 text-slate-500 hover:bg-slate-200 font-black text-[11px] uppercase transition-all shadow-sm rounded-none"
                    >
                        Cancelar y Salir
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="px-12 py-3 bg-[#0066cc] text-white shadow-lg hover:bg-[#004d99] transition-all font-black text-[12px] uppercase tracking-[0.1em] flex items-center gap-2 border border-[#0052a3] shadow-[0_4px_10px_rgba(0,102,204,0.3)] disabled:opacity-50"
                    >
                        {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                        {isEditMode ? 'Guardar Cambios' : 'Crear Nivel'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default NivelFidelidadForm;
